/**
 * The SOPs tab (plan docs/plans/lms-plan.md 11, section 13 tab 4, R5, R25;
 * WP-46). Replaces WP-03's placeholder in place and keeps its default export
 * (LmsApp routes /lms/sops to it; plan 18.3).
 *
 * One route, two views: the library (11.1: every SOP of the network with its
 * number, title, category, owner, the effective version and its date, and
 * the training status) and, with ?sop=<id>, one SOP (the admin's version
 * tracker, or the versions a learner may open). The tab route has no subtree
 * (tabs/config.ts), so the SOP travels in the query string.
 *
 * Everyone opens the effective file (presigned, inline, a new tab, D15). The
 * admin actions (create, upload, make effective, send for training, retire,
 * the acknowledgement tracker) appear only when GET /api/lms/me says
 * isLmsAdmin (4.2); every admin route re-checks it on the server.
 */
import { useCallback, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, Plus } from 'lucide-react';
import PageHeader from '../../../ui/PageHeader';
import { primaryBtnCls } from '../../../ui/styles';
import { useToast } from '../../../ui/Toast';
import { lmsPageCls } from '../../components/layout/page';
import { useLmsMe } from '../../hooks/useLmsMe';
import { lmsTab } from '../config';
import { sopHref, SOP_QUERY_KEY } from './labels';
import { SopDetailView } from './SopDetailView';
import { SopFormModal } from './SopFormModal';
import { SopList } from './SopList';

const TAB = lmsTab('sops');

export function SopsTab() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  // The identity decides which view loads (an admin's library carries training counts and a
  // status filter), so the body waits for it instead of asking twice.
  const { isLmsAdmin, loading: meLoading } = useLmsMe();
  const selected = params.get(SOP_QUERY_KEY);
  const [creating, setCreating] = useState(false);

  const openSop = useCallback((sopId: string) => navigate(sopHref(sopId)), [navigate]);

  return (
    <div className={lmsPageCls} data-testid="lms-sops">
      <PageHeader
        title={TAB.title}
        icon={TAB.icon}
        subtitle={TAB.description}
        actions={
          isLmsAdmin && !selected ? (
            <button type="button" className={primaryBtnCls} onClick={() => setCreating(true)} data-testid="lms-sop-new">
              <Plus className="h-4 w-4" aria-hidden="true" />
              New SOP
            </button>
          ) : null
        }
      />
      {meLoading ? (
        <div className="flex justify-center py-10" data-testid="lms-sops-loading">
          <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
          <span className="sr-only">Loading</span>
        </div>
      ) : selected ? (
        <SopDetailView key={selected} sopId={selected} isAdmin={isLmsAdmin} onOpenSop={openSop} />
      ) : (
        <SopList isAdmin={isLmsAdmin} onOpenSop={openSop} />
      )}
      {isLmsAdmin && (
        <SopFormModal
          mode="create"
          isOpen={creating}
          onClose={() => setCreating(false)}
          onSaved={(sop) => {
            setCreating(false);
            toast(`SOP ${sop.sop_number} created. Upload its first version next.`, { type: 'success' });
            openSop(sop.id);
          }}
          onOpenExisting={(id) => {
            setCreating(false);
            openSop(id);
          }}
        />
      )}
    </div>
  );
}

export default SopsTab;
